<template>
  <div class="admin-dashboard">
    <!-- Security Header -->
    <div class="admin-header">
      <div class="header-content">
        <h1>{{ getLang == 'indo' ? 'Dashboard Admin Crypto' : 'Crypto Admin Dashboard' }}</h1>
        <div class="header-actions">
          <span class="session-info">
            {{ getLang == 'indo' ? 'Sesi berakhir dalam' : 'Session expires in' }}: {{ sessionTimeRemaining }}
          </span>
          <button @click="handleLogout" class="logout-btn">
            {{ getLang == 'indo' ? 'Keluar' : 'Logout' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon pending">📋</div>
        <div class="stat-content">
          <div class="stat-number">{{ stats.pending }}</div>
          <div class="stat-label">{{ getLang == 'indo' ? 'Menunggu' : 'Pending' }}</div>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon approved">✅</div>
        <div class="stat-content">
          <div class="stat-number">{{ stats.approved }}</div>
          <div class="stat-label">{{ getLang == 'indo' ? 'Disetujui' : 'Approved' }}</div>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon rejected">❌</div>
        <div class="stat-content">
          <div class="stat-number">{{ stats.rejected }}</div>
          <div class="stat-label">{{ getLang == 'indo' ? 'Ditolak' : 'Rejected' }}</div>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon total">📊</div>
        <div class="stat-content">
          <div class="stat-number">{{ stats.total }}</div>
          <div class="stat-label">{{ getLang == 'indo' ? 'Total' : 'Total' }}</div>
        </div>
      </div>
    </div>

    <!-- Controls -->
    <div class="controls-section">
      <div class="filters">
        <select v-model="statusFilter" @change="filterSubmissions" class="filter-select">
          <option value="">{{ getLang == 'indo' ? 'Semua Status' : 'All Status' }}</option>
          <option value="pending">{{ getLang == 'indo' ? 'Menunggu' : 'Pending' }}</option>
          <option value="approved">{{ getLang == 'indo' ? 'Disetujui' : 'Approved' }}</option>
          <option value="rejected">{{ getLang == 'indo' ? 'Ditolak' : 'Rejected' }}</option>
        </select>
        
        <input 
          v-model="searchQuery" 
          @input="filterSubmissions"
          :placeholder="getLang == 'indo' ? 'Cari email atau ID...' : 'Search email or ID...'"
          class="search-input"
          maxlength="100"
        >
      </div>
      
      <div class="actions">
        <button @click="refreshData" class="action-btn refresh">
          {{ getLang == 'indo' ? 'Refresh' : 'Refresh' }}
        </button>
        <button @click="exportData" class="action-btn export">
          {{ getLang == 'indo' ? 'Export' : 'Export' }}
        </button>
      </div>
    </div>

    <!-- Submissions List -->
    <div class="submissions-container">
      <div v-if="filteredSubmissions.length === 0" class="no-submissions">
        <div class="empty-state">
          <div class="empty-icon">📭</div>
          <h3>{{ getLang == 'indo' ? 'Tidak ada submisi' : 'No submissions found' }}</h3>
          <p>{{ getLang == 'indo' ? 'Belum ada verifikasi crypto yang disubmit' : 'No crypto verifications have been submitted yet' }}</p>
        </div>
      </div>

      <div v-else class="submissions-list">
        <div 
          v-for="submission in paginatedSubmissions" 
          :key="submission.id"
          class="submission-card"
          :class="{ 'high-priority': isHighPriority(submission) }"
        >
          <!-- Submission Header -->
          <div class="submission-header">
            <div class="submission-info">
              <span class="submission-id">#{{ submission.id }}</span>
              <span class="submission-date">{{ formatDate(submission.createdAt) }}</span>
            </div>
            <div class="status-badge" :class="submission.status">
              {{ formatStatus(submission.status) }}
            </div>
          </div>

          <!-- User Information -->
          <div class="user-section">
            <div class="info-grid">
              <div class="info-item">
                <label>{{ getLang == 'indo' ? 'Email' : 'Email' }}:</label>
                <span>{{ submission.userEmail }}</span>
              </div>
              <div class="info-item">
                <label>{{ getLang == 'indo' ? 'Jumlah' : 'Amount' }}:</label>
                <span class="amount">${{ submission.amount }}</span>
              </div>
              <div class="info-item">
                <label>{{ getLang == 'indo' ? 'Crypto' : 'Crypto' }}:</label>
                <span class="crypto-type">{{ submission.cryptoType }}</span>
              </div>
              <div class="info-item">
                <label>{{ getLang == 'indo' ? 'Akun' : 'Account' }}:</label>
                <span>{{ submission.account || 'N/A' }}</span>
              </div>
            </div>
            
            <div v-if="submission.walletAddress" class="wallet-info">
              <label>{{ getLang == 'indo' ? 'Alamat Wallet' : 'Wallet Address' }}:</label>
              <div class="wallet-address">{{ submission.walletAddress }}</div>
            </div>
            
            <div v-if="submission.transactionId" class="transaction-info">
              <label>{{ getLang == 'indo' ? 'ID Transaksi' : 'Transaction ID' }}:</label>
              <div class="transaction-id">{{ submission.transactionId }}</div>
            </div>
          </div>

          <!-- Proof Section -->
          <div v-if="submission.fileData" class="proof-section">
            <label>{{ getLang == 'indo' ? 'Bukti Pembayaran' : 'Payment Proof' }}:</label>
            <div class="proof-container">
              <img 
                v-if="isImageFile(submission.fileType)"
                :src="submission.fileData" 
                :alt="submission.originalName"
                class="proof-image"
                @click="showImageModal(submission.fileData, submission.originalName)"
              >
              <div v-else class="file-info">
                <span class="file-name">{{ submission.originalName }}</span>
                <span class="file-size">({{ formatFileSize(submission.fileSize) }})</span>
              </div>
            </div>
          </div>

          <!-- Action Section -->
          <div class="action-section">
            <div class="status-controls">
              <select 
                v-model="submission.newStatus" 
                @change="updateSubmissionStatus(submission)"
                class="status-select"
              >
                <option value="pending">{{ getLang == 'indo' ? 'Menunggu' : 'Pending' }}</option>
                <option value="approved">{{ getLang == 'indo' ? 'Setujui' : 'Approve' }}</option>
                <option value="rejected">{{ getLang == 'indo' ? 'Tolak' : 'Reject' }}</option>
              </select>
            </div>
            
            <div class="timestamps">
              <small>
                {{ getLang == 'indo' ? 'Dibuat' : 'Created' }}: {{ formatDateTime(submission.createdAt) }}
                <br v-if="submission.updatedAt !== submission.createdAt">
                <span v-if="submission.updatedAt !== submission.createdAt">
                  {{ getLang == 'indo' ? 'Diupdate' : 'Updated' }}: {{ formatDateTime(submission.updatedAt) }}
                </span>
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination">
        <button 
          @click="currentPage = Math.max(1, currentPage - 1)"
          :disabled="currentPage === 1"
          class="page-btn"
        >
          {{ getLang == 'indo' ? 'Sebelumnya' : 'Previous' }}
        </button>
        
        <span class="page-info">
          {{ getLang == 'indo' ? 'Halaman' : 'Page' }} {{ currentPage }} {{ getLang == 'indo' ? 'dari' : 'of' }} {{ totalPages }}
        </span>
        
        <button 
          @click="currentPage = Math.min(totalPages, currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="page-btn"
        >
          {{ getLang == 'indo' ? 'Selanjutnya' : 'Next' }}
        </button>
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <button @click="closeModal" class="modal-close">&times;</button>
        <img :src="modalImage" :alt="modalImageName" class="modal-image">
        <div class="modal-footer">{{ modalImageName }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import adminAuth from '../services/adminAuth';
import cryptoDatabase from '../services/cryptoDatabase';
import Swal from 'sweetalert2';

export default {
  name: 'AdminDashboard',
  data() {
    return {
      submissions: [],
      filteredSubmissions: [],
      statusFilter: '',
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      sessionTimeRemaining: '30:00',
      showModal: false,
      modalImage: '',
      modalImageName: '',
      getLang: localStorage.getItem('setLang') || 'uk'
    };
  },
  computed: {
    stats() {
      return {
        total: this.submissions.length,
        pending: this.submissions.filter(s => s.status === 'pending').length,
        approved: this.submissions.filter(s => s.status === 'approved').length,
        rejected: this.submissions.filter(s => s.status === 'rejected').length
      };
    },
    
    totalPages() {
      return Math.ceil(this.filteredSubmissions.length / this.itemsPerPage);
    },
    
    paginatedSubmissions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredSubmissions.slice(start, start + this.itemsPerPage);
    }
  },
  mounted() {
    // Check authentication
    if (!adminAuth.isAuthenticated()) {
      this.$router.push('/admin/login');
      return;
    }

    // Load data
    this.loadSubmissions();
    
    // Start session timer
    this.startSessionTimer();
    
    // Set security headers
    this.setSecurityHeaders();
  },
  beforeDestroy() {
    if (this.sessionTimer) {
      clearInterval(this.sessionTimer);
    }
  },
  methods: {
    setSecurityHeaders() {
      // Prevent page caching
      const meta = document.createElement('meta');
      meta.setAttribute('http-equiv', 'Cache-Control');
      meta.setAttribute('content', 'no-cache, no-store, must-revalidate');
      document.head.appendChild(meta);
    },

    loadSubmissions() {
      try {
        this.submissions = cryptoDatabase.getAllSubmissions();
        this.submissions.forEach(submission => {
          submission.newStatus = submission.status;
        });
        this.filterSubmissions();
      } catch (error) {
        console.error('Error loading submissions:', error);
        this.showError(this.getLang === 'indo' ? 'Gagal memuat data' : 'Failed to load data');
      }
    },

    filterSubmissions() {
      let filtered = [...this.submissions];
      
      // Status filter
      if (this.statusFilter) {
        filtered = filtered.filter(s => s.status === this.statusFilter);
      }
      
      // Search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(s => 
          s.userEmail.toLowerCase().includes(query) ||
          s.id.toString().includes(query) ||
          (s.transactionId && s.transactionId.toLowerCase().includes(query))
        );
      }
      
      this.filteredSubmissions = filtered;
      this.currentPage = 1;
    },

    async updateSubmissionStatus(submission) {
      if (submission.newStatus === submission.status) return;

      try {
        const result = await Swal.fire({
          title: this.getLang === 'indo' ? 'Konfirmasi' : 'Confirm',
          text: this.getLang === 'indo' ? 
            `Ubah status menjadi ${this.formatStatus(submission.newStatus)}?` :
            `Change status to ${this.formatStatus(submission.newStatus)}?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: this.getLang === 'indo' ? 'Ya' : 'Yes',
          cancelButtonText: this.getLang === 'indo' ? 'Batal' : 'Cancel'
        });

        if (result.isConfirmed) {
          cryptoDatabase.updateSubmissionStatus(submission.id, submission.newStatus);
          submission.status = submission.newStatus;
          submission.updatedAt = new Date().toISOString();
          
          this.showSuccess(this.getLang === 'indo' ? 'Status berhasil diupdate' : 'Status updated successfully');
        } else {
          submission.newStatus = submission.status; // Revert
        }
      } catch (error) {
        console.error('Error updating status:', error);
        submission.newStatus = submission.status; // Revert
        this.showError(this.getLang === 'indo' ? 'Gagal mengupdate status' : 'Failed to update status');
      }
    },

    refreshData() {
      this.loadSubmissions();
      this.showSuccess(this.getLang === 'indo' ? 'Data berhasil direfresh' : 'Data refreshed successfully');
    },

    exportData() {
      try {
        const exportData = cryptoDatabase.exportData();
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `crypto-submissions-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        
        this.showSuccess(this.getLang === 'indo' ? 'Data berhasil diexport' : 'Data exported successfully');
      } catch (error) {
        console.error('Export error:', error);
        this.showError(this.getLang === 'indo' ? 'Gagal export data' : 'Failed to export data');
      }
    },

    startSessionTimer() {
      this.sessionTimer = setInterval(() => {
        if (!adminAuth.isAuthenticated()) {
          this.$router.push('/admin/login');
          return;
        }
        // Update session remaining time display logic here
      }, 60000); // Check every minute
    },

    async handleLogout() {
      const result = await Swal.fire({
        title: this.getLang === 'indo' ? 'Keluar Admin' : 'Admin Logout',
        text: this.getLang === 'indo' ? 'Yakin ingin keluar?' : 'Are you sure you want to logout?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.getLang === 'indo' ? 'Ya' : 'Yes',
        cancelButtonText: this.getLang === 'indo' ? 'Batal' : 'Cancel'
      });

      if (result.isConfirmed) {
        adminAuth.logout();
        this.$router.push('/admin/login');
      }
    },

    showImageModal(imageData, imageName) {
      this.modalImage = imageData;
      this.modalImageName = imageName;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.modalImage = '';
      this.modalImageName = '';
    },

    isImageFile(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    isHighPriority(submission) {
      return submission.amount > 1000 || submission.status === 'pending';
    },

    formatStatus(status) {
      const statusMap = {
        pending: this.getLang === 'indo' ? 'Menunggu' : 'Pending',
        approved: this.getLang === 'indo' ? 'Disetujui' : 'Approved',
        rejected: this.getLang === 'indo' ? 'Ditolak' : 'Rejected'
      };
      return statusMap[status] || status;
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    },

    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString();
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },

    showSuccess(message) {
      Swal.fire({
        icon: 'success',
        title: message,
        timer: 2000,
        showConfirmButton: false
      });
    },

    showError(message) {
      Swal.fire({
        icon: 'error',
        title: this.getLang === 'indo' ? 'Error' : 'Error',
        text: message
      });
    }
  }
};
</script>

<style scoped>
.admin-dashboard {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 20px;
}

.admin-header {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 24px;
  padding: 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content h1 {
  margin: 0;
  color: #333;
  font-size: 28px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.session-info {
  color: #666;
  font-size: 14px;
}

.logout-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.logout-btn:hover {
  background: #c82333;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  font-size: 32px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon.pending { background: #fff3cd; }
.stat-icon.approved { background: #d4edda; }
.stat-icon.rejected { background: #f8d7da; }
.stat-icon.total { background: #cce5ff; }

.stat-number {
  font-size: 32px;
  font-weight: 700;
  color: #333;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.controls-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-select, .search-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.search-input {
  min-width: 200px;
}

.actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.action-btn.refresh {
  background: #007bff;
  color: white;
}

.action-btn.export {
  background: #28a745;
  color: white;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

.submissions-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

.no-submissions {
  padding: 60px 20px;
  text-align: center;
}

.empty-state {
  max-width: 400px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state h3 {
  color: #333;
  margin-bottom: 8px;
}

.empty-state p {
  color: #666;
}

.submissions-list {
  padding: 0;
}

.submission-card {
  border-bottom: 1px solid #eee;
  padding: 24px;
  transition: background-color 0.2s;
}

.submission-card:hover {
  background-color: #f8f9fa;
}

.submission-card.high-priority {
  border-left: 4px solid #ff6b6b;
}

.submission-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.submission-id {
  font-weight: 600;
  color: #007bff;
}

.submission-date {
  color: #666;
  font-size: 14px;
  margin-left: 12px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
}

.status-badge.approved {
  background: #d4edda;
  color: #155724;
}

.status-badge.rejected {
  background: #f8d7da;
  color: #721c24;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.info-item {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
}

.info-item label {
  font-weight: 600;
  color: #495057;
  font-size: 12px;
  text-transform: uppercase;
  display: block;
  margin-bottom: 4px;
}

.amount {
  color: #28a745;
  font-weight: 600;
}

.crypto-type {
  background: #007bff;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.wallet-info, .transaction-info {
  margin-bottom: 12px;
}

.wallet-address, .transaction-id {
  background: #f8f9fa;
  padding: 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  word-break: break-all;
  margin-top: 4px;
}

.proof-section {
  margin: 16px 0;
}

.proof-container {
  margin-top: 8px;
}

.proof-image {
  max-width: 200px;
  max-height: 150px;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.2s;
}

.proof-image:hover {
  transform: scale(1.05);
}

.file-info {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.status-select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.timestamps {
  text-align: right;
}

.timestamps small {
  color: #666;
  font-size: 12px;
}

.pagination {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-top: 1px solid #eee;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  color: #666;
  font-size: 14px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 90vw;
  max-height: 90vh;
  position: relative;
  overflow: hidden;
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 20px;
  cursor: pointer;
  z-index: 1001;
}

.modal-image {
  max-width: 100%;
  max-height: calc(90vh - 60px);
  display: block;
}

.modal-footer {
  padding: 12px 16px;
  background: #f8f9fa;
  font-size: 14px;
  color: #666;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .admin-dashboard {
    padding: 12px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .header-content h1 {
    font-size: 24px;
  }
  
  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
  }
  
  .stat-card {
    padding: 16px;
  }
  
  .controls-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters {
    justify-content: center;
  }
  
  .search-input {
    min-width: auto;
    flex: 1;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .action-section {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .timestamps {
    text-align: left;
  }
}
</style>